import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, EMPTY, firstValueFrom, map, type Observable } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './util.service';
import { type NotificationAlert } from '../models/notification-alert.model';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from './storage.service';
import { StorageEnum } from '../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { BiometricsService } from './biometrics.service';
import { App, type AppInfo } from '@capacitor/app';
import { type AccountDetail } from '../models/account-detail';
import { type UserProfile } from '../models/user-profile';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AuthService } from './auth.service';
import { type MobileDeviceInfo, type AccountInfo } from '../models/mobile-device-info';

@Injectable({
  providedIn: 'root',
})
export class AlertsService {
  deviceId = '';
  env = environment;
  mobileDeviceInfo: MobileDeviceInfo | undefined;
  accountInfo: AccountInfo | undefined;
  private listenerHandles: any[] = [];
  private listenersInitialized = false;

  constructor(
    private readonly authS: AuthService,
    private readonly bioS: BiometricsService,
    private readonly http: HttpClient,
    private readonly storageS: StorageService,
    private readonly utils: UtilService,
  ) {}

  GetUserPreferences(accountID: string, userID: string): Observable<NotificationAlert[]> {
    const url = environment.OASUrl() + '/Notification/GetUserPreferencesByAccountId';
    const data = {
      accountId: accountID,
      userId: userID,
    };

    return this.http
      .get<NotificationAlert[]>(url, {
        headers: { ...environment.requestKeysAPP },
        params: data,
        responseType: 'json',
      })
      .pipe(
        catchError((e) => {
          this.utils.consoleGroup('get error: ', e);
          // server error. ugly error page??
          throw new Error(e);
        }),
        map((data: any) => {
          if (data.Response?.ReasonPhrase?.includes('unauthorized operation')) {
            // unauth - log user out
          } else {
            // error message ??
          }
          return data;
        }),
      );
  }

  UpsertPreferences(data: any): Observable<any> {
    const url = environment.OASUrl() + '/Notification/InsertClientPreference';

    return this.http
      .post(url, data, {
        headers: { ...environment.requestKeysAPP },
        responseType: 'json',
      })
      .pipe(
        catchError((e) => {
          this.utils.consoleGroup('upsert error: ', e);
          // server error. ugly error page??
          throw new Error(e);
        }),
        map((data: any) => {
          if (data.Response?.ReasonPhrase?.includes('unauthorized operation')) {
            // unauth - log user out
          }
          return data;
        }),
      );
  }

  /**
   * Push Notifications
   *
   * IMPORTANT NATIVE SETUP REQUIREMENTS:
   *
   * iOS:
   * 1. Enable Push Notifications capability in Xcode
   * 2. Add the following to AppDelegate.swift:
   *
   *    func application(_ application: UIApplication,
   *                    didRegisterForRemoteNotificationsWithDeviceToken deviceToken: Data) {
   *      NotificationCenter.default.post(name: .capacitorDidRegisterForRemoteNotifications, object: deviceToken)
   *    }
   *
   *    func application(_ application: UIApplication,
   *                    didFailToRegisterForRemoteNotificationsWithError error: Error) {
   *      NotificationCenter.default.post(name: .capacitorDidFailToRegisterForRemoteNotifications, object: error)
   *    }
   *
   * Android:
   * 1. Set up Firebase Cloud Messaging (FCM)
   * 2. Add google-services.json to android/app/ directory
   * 3. Ensure firebase-messaging dependency is included (handled by Capacitor)
   *
   * See: https://capacitorjs.com/docs/v7/apis/push-notifications
   */
  logAccountInfo(accountInfo: AccountInfo): Observable<any> {
    const url = environment.createCSRUrl() + '/Registration/SaveUserAccount';
    return this.http.post(url, accountInfo, {
      headers: new HttpHeaders(environment.requestKeysAPP),
      responseType: 'json',
    });
  }

  saveDeviceToken(deviceToken: any): Observable<any> {
    const url = environment.NotificationsUrl() + '/notification/SaveDeviceToken';

    return this.http.post(url, deviceToken, {
      headers: new HttpHeaders(environment.requestKeysAPP),
      responseType: 'json',
    });
  }

  /**
   * Initialize push notification listeners.
   * Should be called once during app initialization, before registration.
   */
  async initializePushNotificationListeners(): Promise<void> {
    if (this.bioS.bioInfo.Platform.toLowerCase() === 'web' || this.listenersInitialized) {
      return;
    }

    this.utils.consoleLog('Initializing push notification listeners...');

    try {
      // Registration success listener
      const registrationHandle = await PushNotifications.addListener(
        'registration',
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (token: Token) => {
          // Only save the token if we've explicitly requested permission
          // This prevents Firebase from auto-registering before the user has been prompted
          const hasAskedPermission = this.storageS.getLocal(StorageEnum.NotificationPermissionAsked) === 'true';
          if (!hasAskedPermission) {
            this.utils.consoleLog(
              'Registration listener fired but permission not explicitly requested, ignoring token',
            );
            return;
          }

          const info = { DeviceId: this.deviceId, NotificationToken: token.value };
          this.storageS.removeLocal(StorageEnum.NotificationPermissionDenied);
          this.storageS.setLocal(StorageEnum.NotificationTokenValue, token.value);
          this.utils.consoleLog('Push notification registration successful, token: ' + token.value);

          try {
            await firstValueFrom(this.saveDeviceToken(info));
            this.utils.consoleLog('Device token saved to server successfully');
          } catch (error) {
            this.utils.consoleGroup('Failed to save device token to server:', error);
          }
        },
      );
      this.listenerHandles.push(registrationHandle);
      this.utils.consoleLog('Registration listener attached');

      // Registration error listener
      const registrationErrorHandle = await PushNotifications.addListener('registrationError', (error: any) => {
        this.utils.consoleGroup('Push notification registration error: ', error);

        // Provide platform-specific troubleshooting info
        const platform = this.bioS.bioInfo.Platform.toLowerCase();
        if (platform === 'ios') {
          this.utils.consoleLog(
            'iOS troubleshooting: Check that Push Notifications capability is enabled and AppDelegate.swift is configured correctly',
          );
        } else if (platform === 'android') {
          this.utils.consoleLog(
            'Android troubleshooting: Verify google-services.json exists and Firebase is configured',
          );
        }
      });
      this.listenerHandles.push(registrationErrorHandle);
      this.utils.consoleLog('Registration error listener attached');

      // Notification received listener (when app is in foreground)
      const notificationReceivedHandle = await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          this.utils.consoleGroup('Push notification received: ', notification);
          alert(`${notification.title}: ${notification.body}`);
        },
      );
      this.listenerHandles.push(notificationReceivedHandle);
      this.utils.consoleLog('Notification received listener attached');

      // Notification action performed listener (when user taps notification)
      const notificationActionHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          this.utils.consoleGroup('Push notification action performed: ', notification);
          const title = notification.notification.data?.title || notification.notification.title;
          const body = notification.notification.data?.body || notification.notification.body;
          alert(`${title}: ${body}`);
        },
      );
      this.listenerHandles.push(notificationActionHandle);
      this.utils.consoleLog('Notification action listener attached');

      this.listenersInitialized = true;
      this.utils.consoleLog('All push notification listeners initialized successfully');
    } catch (error) {
      this.utils.consoleGroup('Error initializing push notification listeners: ', error);
      throw error; // Re-throw to prevent registration attempt with incomplete setup
    }
  }

  /**
   * Clean up push notification listeners.
   * Call on service destruction or user logout.
   */
  async cleanupPushNotificationListeners(): Promise<void> {
    try {
      for (const handle of this.listenerHandles) {
        await handle.remove();
      }
      this.listenerHandles = [];
      this.listenersInitialized = false;
      this.utils.consoleLog('Push notification listeners cleaned up');
    } catch (error) {
      this.utils.consoleGroup('Error cleaning up push notification listeners: ', error);
    }
  }

  /**
   * Create Android notification channel for Android 8+ (API 26+).
   * Ensures notifications display properly on modern Android devices.
   */
  private async createAndroidNotificationChannel(): Promise<void> {
    if (this.bioS.bioInfo.Platform.toLowerCase() !== 'android') {
      return;
    }

    try {
      await PushNotifications.createChannel({
        id: 'default',
        name: 'Default Notifications',
        description: 'General app notifications',
        importance: 4, // High importance
        visibility: 1, // Public
        sound: 'default',
        vibration: true,
        lights: true,
      });
      this.utils.consoleLog('Android notification channel created successfully');
    } catch (error) {
      this.utils.consoleGroup('Error creating Android notification channel: ', error);
    }
  }

  /**
   * Request push notification permissions and register device.
   * Follows Capacitor recommended pattern: checkPermissions -> requestPermissions -> register.
   * On Android 12 and below, permission is granted by default so we track explicit asks.
   */
  private async GetRegistrationToken(): Promise<void> {
    try {
      // Check current permission status first
      let permStatus = await PushNotifications.checkPermissions();
      this.utils.consoleGroup('Current permission status: ', permStatus);

      // Check if we've already explicitly asked for permission
      const hasAskedBefore = this.storageS.getLocal(StorageEnum.NotificationPermissionAsked) === 'true';

      // Request permissions if not yet determined or if rationale should be shown (Android 13+)
      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        this.utils.consoleLog('Requesting push notification permissions');
        this.storageS.setLocal(StorageEnum.NotificationPermissionAsked, 'true');
        permStatus = await PushNotifications.requestPermissions();
        this.utils.consoleGroup('Permission request result: ', permStatus);
      } else if (permStatus.receive === 'granted' && !hasAskedBefore) {
        // On Android 12 and below, permission is granted by default without prompting.
        // We still need to explicitly ask to trigger the system prompt on older devices,
        // or at minimum track that we've "asked" so the user experience is consistent.
        this.utils.consoleLog('Permission already granted (Android 12 or below), requesting to confirm');
        this.storageS.setLocal(StorageEnum.NotificationPermissionAsked, 'true');
        permStatus = await PushNotifications.requestPermissions();
        this.utils.consoleGroup('Permission confirmation result: ', permStatus);
      }

      if (permStatus.receive !== 'granted') {
        this.utils.consoleLog('Push notification permission denied or not granted');
        this.storageS.setLocal(StorageEnum.NotificationPermissionDenied, 'true');
        return;
      }

      // Only proceed with registration if we've been through the permission flow
      if (!hasAskedBefore && this.storageS.getLocal(StorageEnum.NotificationPermissionAsked) !== 'true') {
        this.utils.consoleLog('Permission flow not completed, skipping registration');
        return;
      }

      this.utils.consoleLog('Push notification permission granted, preparing to register');

      // Small delay to ensure listeners are fully attached before registering
      // This prevents race conditions where native events fire before listeners are ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      this.utils.consoleLog('Calling PushNotifications.register()...');

      // Register with Apple / Google to receive push via APNS/FCM
      // Wrap in additional try-catch to capture native errors
      try {
        await PushNotifications.register();
        this.utils.consoleLog('PushNotifications.register() completed successfully');
      } catch (registerError: any) {
        this.utils.consoleGroup('Error calling PushNotifications.register():', registerError);

        // Log detailed error information
        if (registerError?.message) {
          this.utils.consoleLog('Register error message: ' + registerError.message);
        }
        if (registerError?.stack) {
          this.utils.consoleLog('Register error stack: ' + registerError.stack);
        }

        // Check for common configuration issues
        const platform = this.bioS.bioInfo.Platform.toLowerCase();
        if (platform === 'ios') {
          this.utils.consoleLog(
            'iOS SETUP REQUIRED: Ensure AppDelegate.swift includes the required PushNotifications code. See: https://capacitorjs.com/docs/v7/apis/push-notifications#ios',
          );
        } else if (platform === 'android') {
          this.utils.consoleLog(
            'ANDROID SETUP REQUIRED: Ensure google-services.json is added to your project and Firebase is configured. See: https://capacitorjs.com/docs/v7/apis/push-notifications#android',
          );
        }

        throw registerError; // Re-throw to be caught by outer catch
      }

      // Create Android notification channel for Android 8+
      await this.createAndroidNotificationChannel();
    } catch (error: any) {
      this.utils.consoleGroup('Error during push notification registration: ', error);

      // Store error state so we don't keep trying if there's a configuration issue
      this.storageS.setLocal(StorageEnum.NotificationPermissionDenied, 'true');
    }
  }

  // 2 refs in app component for update checking and dark mode
  async GetDeviceInfo(): Promise<MobileDeviceInfo> {
    const deviceInfo: any = await Device.getInfo();
    const deviceId = (await Device.getId()).identifier;
    deviceInfo.uuid = deviceId; // Ensure uuid is set for mapping
    this.bioS.bioInfo.DeviceId = deviceId || '';
    this.bioS.bioInfo.Platform = deviceInfo.platform ? deviceInfo.platform : '';
    if (this.bioS.bioInfo.Platform.toLowerCase() !== 'web') {
      const appInfo = await this.GetAppInfo();
      return this.mapDeviceInfo(deviceInfo, appInfo.build, appInfo.version);
    } else {
      return this.mapDeviceInfo(deviceInfo);
    }
  }

  async GetAppInfo(): Promise<AppInfo> {
    const appInfo: any = await App.getInfo();
    return appInfo;
  }

  async GetAccountInfo(deviceId: string): Promise<AccountInfo | undefined> {
    // if (!this.auth.validAccountNumber(null, false)) {
    //   this.auth.signOut(true);
    //   return;
    // }

    const accDetail: AccountDetail = this.storageS.getSession(StorageEnum.AccountDetail);
    const loginInfo: UserProfile = this.storageS.getSession(StorageEnum.LoginResponse);

    // Use AccountDetail if available, otherwise fall back to LoginResponse for account number
    const accountId = accDetail?.AccountNumber || loginInfo?.Accounts?.[0]?.AccountNumber || '';
    const userId = loginInfo?.UserId || '';

    if (!accountId || !userId) {
      this.utils.consoleLog('GetAccountInfo: Missing account or user info');
      return undefined;
    }

    return this.mapAccountInfo(deviceId, accountId, userId);
  }

  /**
   * Initialize device and register for push notifications if needed.
   * Sets up listeners before attempting registration.
   */
  async GetDeviceToken(): Promise<void> {
    try {
      let deviceInfo;
      if (this.bioS.bioInfo.DeviceId === '' || this.bioS.bioInfo.Platform === '') {
        deviceInfo = await Device.getInfo();
        const deviceIId = (await Device.getId()).identifier;
        this.bioS.bioInfo.DeviceId = deviceIId || '';
        this.bioS.bioInfo.Platform = deviceInfo.platform ? deviceInfo.platform : '';
      }

      if (this.bioS.bioInfo.Platform.toLowerCase() !== 'web') {
        this.deviceId = this.bioS.bioInfo.DeviceId;

        this.utils.consoleLog(`Initializing push notifications on platform: ${this.bioS.bioInfo.Platform}`);

        // Initialize listeners first, before any registration attempt
        try {
          await this.initializePushNotificationListeners();
        } catch (listenerError) {
          this.utils.consoleGroup(
            'Failed to initialize push notification listeners, aborting registration:',
            listenerError,
          );
          return; // Don't attempt registration if listeners failed to initialize
        }

        // Log user id, account id and device Id
        const accountInfo: AccountInfo | undefined = await this.GetAccountInfo(this.deviceId);
        if (accountInfo === undefined) {
          this.utils.consoleLog('GetDeviceToken: Account info unavailable, cannot proceed');
          return;
        }

        try {
          await firstValueFrom(this.logAccountInfo(accountInfo));
          this.utils.consoleLog('Account info logged successfully');
        } catch (accountError) {
          this.utils.consoleGroup('Failed to log account info:', accountError);
          // Continue anyway - this shouldn't block push notification setup
        }

        const notificationToken = await this.storageS.getLocal(StorageEnum.NotificationTokenValue);
        const permissionDenied = (await this.storageS.getLocal(StorageEnum.NotificationPermissionDenied)) === 'true';

        this.utils.consoleGroup('GetDeviceToken called, notification token from storage:', notificationToken);

        if (permissionDenied) {
          this.utils.consoleLog('GetDeviceToken called, permission previously denied for push notifications');
          return;
        }

        if (notificationToken === null || notificationToken === '') {
          this.utils.consoleLog(
            'GetDeviceToken called, no notification token found, registering for push notifications',
          );
          await this.GetRegistrationToken();
        } else {
          this.utils.consoleLog('GetDeviceToken called, notification token already exists, skipping registration');
        }
      }
    } catch (error) {
      this.utils.consoleGroup('Error in GetDeviceToken: ', error);
    }
  }

  // called to set device id in alert comp. shouldnt be necessary if it's set in app component
  async GetDeviceId(): Promise<string> {
    // alert(registerForPush);
    let deviceInfo;
    if (this.bioS.bioInfo.DeviceId === '' || this.bioS.bioInfo.Platform === '') {
      deviceInfo = await Device.getInfo();
      const deviceId = (await Device.getId()).identifier;
      this.bioS.bioInfo.DeviceId = deviceId || '';
      this.bioS.bioInfo.Platform = deviceInfo.platform ? deviceInfo.platform : '';
    }
    return this.bioS.bioInfo.DeviceId;
  }

  mapDeviceInfo(info: any, build: any = '', version: any = ''): MobileDeviceInfo {
    this.mobileDeviceInfo = {
      DeviceId: info.uuid === '' ? null : info.uuid,
      DeviceAppVersion: version === '' ? null : version,
      DeviceAppBuild: build === '' ? null : build,
      DeviceOperatingSystem: info.operatingSystem === '' ? null : info.operatingSystem,
      DeviceOSVersion: info.osVersion === '' ? null : info.osVersion,
      DevicePlatform: info.platform === '' ? null : info.platform,
      DeviceModel: info.model === '' ? null : info.model,
      DeviceManufacturer: info.manufacturer === '' ? null : info.manufacturer,
      Notifications: false,
    };
    return this.mobileDeviceInfo;
  }

  mapAccountInfo(deviceId: string, accountId: string, userId: string): AccountInfo {
    this.accountInfo = {
      DeviceId: deviceId === '' ? null : deviceId,
      AccountId: accountId === '' ? null : accountId,
      UserId: userId === '' ? null : userId,
    };
    return this.accountInfo;
  }

  registerDevice(deviceInfoObject: MobileDeviceInfo): Observable<any> {
    const url = environment.createCSRUrl() + '/Registration/RegisterMobileDevice';
    return this.http.post(url, deviceInfoObject, {
      headers: new HttpHeaders(environment.requestKeysAPP),
      responseType: 'json',
    });
  }

  sendTestNotification(data: any): Observable<any> {
    const url = environment.NotificationsUrl() + '/notification/SendTestNotification';
    const params = {
      deviceId: data.deviceId,
      token: data.token,
      title: data.title,
      body: data.body,
    };
    return this.http
      .get(url, {
        headers: new HttpHeaders(environment.requestKeysAPP),
        params,
        responseType: 'json',
      })
      .pipe(
        catchError((e) => {
          this.utils.consoleGroup('send test notification error: ', e);
          return EMPTY;
        }),
      );
  }
}
